import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scale,
  Users,
  Plus,
  FileText,
  Calendar,
  Clock,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  Gavel,
  UserPlus,
  Building2,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronRight,
  ArrowRight,
  Bot,
  Sparkles,
  Play,
  Pause,
  Send,
  Loader2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// حالات القضايا
const caseStatuses = {
  pending: { label: 'قيد الانتظار', color: 'bg-yellow-500', textColor: 'text-yellow-500' },
  active: { label: 'جارية', color: 'bg-blue-500', textColor: 'text-blue-500' },
  adjourned: { label: 'مؤجلة', color: 'bg-orange-500', textColor: 'text-orange-500' },
  closed: { label: 'مغلقة', color: 'bg-green-500', textColor: 'text-green-500' },
  cancelled: { label: 'ملغاة', color: 'bg-red-500', textColor: 'text-red-500' },
};

// أنواع القضايا
const caseTypes = [
  { id: 'commercial', name: 'تجاري' },
  { id: 'labor', name: 'عمالي' },
  { id: 'real_estate', name: 'عقاري' },
  { id: 'family', name: 'أحوال شخصية' },
  { id: 'civil', name: 'مدني' },
  { id: 'criminal', name: 'جزائي' },
];

// أدوار الأطراف
const partyRoles = [
  { id: 'plaintiff', name: 'مدعي', color: 'bg-blue-500' },
  { id: 'defendant', name: 'مدعى عليه', color: 'bg-green-500' },
  { id: 'witness', name: 'شاهد', color: 'bg-purple-500' },
  { id: 'expert', name: 'خبير', color: 'bg-orange-500' },
  { id: 'representative', name: 'وكيل', color: 'bg-teal-500' },
];

export default function ArbitratorPanel() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('cases'); // cases, sessions, parties, ai
  const [cases, setCases] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewCaseModal, setShowNewCaseModal] = useState(false);
  const [showNewPartyModal, setShowNewPartyModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // AI Chat State
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [selectedAiRole, setSelectedAiRole] = useState('defendant');
  
  // New Case Form
  const [newCase, setNewCase] = useState({
    title: '',
    case_number: '',
    case_type: '',
    description: '',
    parties: [],
  });
  
  // New Party Form
  const [newParty, setNewParty] = useState({
    name: '',
    role: '',
    email: '',
    phone: '',
    id_number: '',
    is_ai: false,
  });

  useEffect(() => {
    const userData = localStorage.getItem('virtualCourtUser');
    const token = localStorage.getItem('virtualCourtToken');
    
    if (!token || !userData) {
      navigate('/virtual-court');
      return;
    }
    
    setUser(JSON.parse(userData));
    fetchCases();
  }, [navigate]);

  const fetchCases = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('virtualCourtToken');
      const response = await fetch(`${API}/api/virtual-court/arbitrator/cases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCases(data.cases || []);
      }
    } catch (err) {
      console.error('Error fetching cases:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCase = async () => {
    try {
      const token = localStorage.getItem('virtualCourtToken');
      const response = await fetch(`${API}/api/virtual-court/arbitrator/cases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newCase)
      });
      
      if (response.ok) {
        setShowNewCaseModal(false);
        setNewCase({ title: '', case_number: '', case_type: '', description: '', parties: [] });
        fetchCases();
      }
    } catch (err) {
      console.error('Error creating case:', err);
    }
  };

  const handleAddParty = () => {
    if (!newParty.name || !newParty.role) return;
    
    setNewCase(prev => ({
      ...prev,
      parties: [...prev.parties, { ...newParty, id: Date.now() }]
    }));
    
    setNewParty({ name: '', role: '', email: '', phone: '', id_number: '', is_ai: false });
    setShowNewPartyModal(false);
  };

  const handleRemoveParty = (partyId) => {
    setNewCase(prev => ({
      ...prev,
      parties: prev.parties.filter(p => p.id !== partyId)
    }));
  };

  const handleAiChat = async () => {
    if (!aiInput.trim() || !selectedCase) return;
    
    const userMessage = { role: 'user', content: aiInput, timestamp: new Date() };
    setAiMessages(prev => [...prev, userMessage]);
    setAiInput('');
    setAiLoading(true);
    
    try {
      const token = localStorage.getItem('virtualCourtToken');
      const response = await fetch(`${API}/api/virtual-court/ai/simulate-party`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          case_id: selectedCase.id,
          party_role: selectedAiRole,
          message: aiInput,
          context: selectedCase.description
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAiMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.response,
          party_role: selectedAiRole,
          timestamp: new Date()
        }]);
      }
    } catch (err) {
      console.error('Error with AI:', err);
    } finally {
      setAiLoading(false);
    }
  };

  const filteredCases = cases.filter(c => {
    const matchesSearch = c.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.case_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-slate-900" dir="rtl">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/virtual-court')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowRight className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center">
                  <Scale className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">لوحة تحكم المحكم</h1>
                  <p className="text-slate-400 text-sm">إدارة قضايا التحكيم</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-slate-300">{user?.name}</span>
              <button
                onClick={() => {
                  localStorage.removeItem('virtualCourtToken');
                  localStorage.removeItem('virtualCourtUser');
                  navigate('/virtual-court');
                }}
                className="px-4 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30"
              >
                خروج
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            {[
              { id: 'cases', label: 'القضايا', icon: FileText },
              { id: 'sessions', label: 'الجلسات', icon: Calendar },
              { id: 'parties', label: 'الأطراف', icon: Users },
              { id: 'ai', label: 'الذكاء الاصطناعي', icon: Bot },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'border-amber-500 text-amber-400'
                    : 'border-transparent text-slate-400 hover:text-white'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Cases Tab */}
        {activeTab === 'cases' && (
          <div className="space-y-6">
            {/* Header Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="flex gap-4">
                <div className="relative flex-1 min-w-[300px]">
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="البحث في القضايا..."
                    className="w-full pr-10 pl-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:border-amber-500 focus:outline-none"
                >
                  <option value="all">جميع الحالات</option>
                  {Object.entries(caseStatuses).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>
              
              <button
                onClick={() => setShowNewCaseModal(true)}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl hover:from-amber-600 hover:to-amber-700 font-medium"
                data-testid="new-case-btn"
              >
                <Plus className="w-5 h-5" />
                قضية جديدة
              </button>
            </div>

            {/* Cases Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
              </div>
            ) : filteredCases.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCases.map(caseItem => (
                  <div
                    key={caseItem.id}
                    className="bg-slate-800 rounded-2xl p-6 border border-slate-700 hover:border-amber-500/50 transition-all cursor-pointer"
                    onClick={() => setSelectedCase(caseItem)}
                    data-testid={`case-card-${caseItem.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${caseStatuses[caseItem.status]?.color} text-white`}>
                        {caseStatuses[caseItem.status]?.label}
                      </div>
                      <button className="text-slate-400 hover:text-white">
                        <MoreVertical className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <h3 className="text-lg font-bold text-white mb-2">{caseItem.title}</h3>
                    <p className="text-slate-400 text-sm mb-4">رقم القضية: {caseItem.case_number}</p>
                    
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {caseItem.parties?.length || 0} طرف
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {caseItem.sessions?.length || 0} جلسة
                      </span>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-slate-700 flex items-center justify-between">
                      <span className="text-xs text-slate-500">
                        {caseTypes.find(t => t.id === caseItem.case_type)?.name || 'غير محدد'}
                      </span>
                      <ChevronRight className="w-5 h-5 text-amber-500" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-slate-800/50 rounded-2xl">
                <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">لا توجد قضايا</h3>
                <p className="text-slate-400 mb-6">ابدأ بإنشاء قضيتك الأولى</p>
                <button
                  onClick={() => setShowNewCaseModal(true)}
                  className="px-6 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600"
                >
                  إنشاء قضية جديدة
                </button>
              </div>
            )}
          </div>
        )}

        {/* AI Tab */}
        {activeTab === 'ai' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-purple-600/20 to-indigo-600/20 rounded-2xl p-6 border border-purple-500/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">الذكاء الاصطناعي القانوني</h3>
                  <p className="text-slate-400 text-sm">محاكاة أطراف القضية الغائبين</p>
                </div>
              </div>
              
              {!selectedCase ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                  <p className="text-slate-300">يرجى اختيار قضية من تبويب القضايا أولاً</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-xl">
                    <span className="text-slate-300">القضية الحالية:</span>
                    <span className="text-white font-medium">{selectedCase.title}</span>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <span className="text-slate-300">محاكاة دور:</span>
                    <select
                      value={selectedAiRole}
                      onChange={(e) => setSelectedAiRole(e.target.value)}
                      className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    >
                      {partyRoles.map(role => (
                        <option key={role.id} value={role.id}>{role.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* AI Chat Interface */}
            {selectedCase && (
              <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
                <div className="h-96 overflow-y-auto p-4 space-y-4">
                  {aiMessages.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <Bot className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>ابدأ المحادثة مع الذكاء الاصطناعي</p>
                      <p className="text-sm mt-2">سيقوم بمحاكاة دور {partyRoles.find(r => r.id === selectedAiRole)?.name}</p>
                    </div>
                  ) : (
                    aiMessages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[80%] p-4 rounded-2xl ${
                          msg.role === 'user'
                            ? 'bg-amber-500 text-white'
                            : 'bg-slate-700 text-slate-100'
                        }`}>
                          {msg.role === 'assistant' && (
                            <div className="flex items-center gap-2 mb-2 text-xs text-slate-400">
                              <Bot className="w-4 h-4" />
                              <span>{partyRoles.find(r => r.id === msg.party_role)?.name}</span>
                            </div>
                          )}
                          <p>{msg.content}</p>
                        </div>
                      </div>
                    ))
                  )}
                  {aiLoading && (
                    <div className="flex justify-start">
                      <div className="bg-slate-700 p-4 rounded-2xl">
                        <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="p-4 border-t border-slate-700 bg-slate-800/50">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={aiInput}
                      onChange={(e) => setAiInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAiChat()}
                      placeholder="اكتب سؤالك أو مرافعتك..."
                      className="flex-1 px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white focus:border-amber-500 focus:outline-none"
                      disabled={aiLoading}
                    />
                    <button
                      onClick={handleAiChat}
                      disabled={aiLoading || !aiInput.trim()}
                      className="px-6 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 disabled:opacity-50"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="text-center py-20 bg-slate-800/50 rounded-2xl">
            <Calendar className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">إدارة الجلسات</h3>
            <p className="text-slate-400">اختر قضية لعرض جلساتها</p>
          </div>
        )}

        {/* Parties Tab */}
        {activeTab === 'parties' && (
          <div className="text-center py-20 bg-slate-800/50 rounded-2xl">
            <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">إدارة الأطراف</h3>
            <p className="text-slate-400">اختر قضية لعرض أطرافها</p>
          </div>
        )}
      </main>

      {/* New Case Modal */}
      {showNewCaseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8">
          <div className="bg-slate-800 rounded-2xl p-8 w-full max-w-2xl mx-4 border border-slate-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">إنشاء قضية جديدة</h3>
              <button
                onClick={() => setShowNewCaseModal(false)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-300 mb-2">عنوان القضية</label>
                  <input
                    type="text"
                    value={newCase.title}
                    onChange={(e) => setNewCase(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                    placeholder="عنوان القضية"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 mb-2">رقم القضية</label>
                  <input
                    type="text"
                    value={newCase.case_number}
                    onChange={(e) => setNewCase(prev => ({ ...prev, case_number: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                    placeholder="رقم القضية"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-slate-300 mb-2">نوع القضية</label>
                <select
                  value={newCase.case_type}
                  onChange={(e) => setNewCase(prev => ({ ...prev, case_type: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                >
                  <option value="">-- اختر نوع القضية --</option>
                  {caseTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-slate-300 mb-2">وصف القضية</label>
                <textarea
                  value={newCase.description}
                  onChange={(e) => setNewCase(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none resize-none"
                  placeholder="وصف تفصيلي للقضية..."
                />
              </div>
              
              {/* Parties Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-slate-300">أطراف القضية</label>
                  <button
                    onClick={() => setShowNewPartyModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600/20 text-purple-400 rounded-lg hover:bg-purple-600/30"
                  >
                    <UserPlus className="w-4 h-4" />
                    إضافة طرف
                  </button>
                </div>
                
                {newCase.parties.length > 0 ? (
                  <div className="space-y-2">
                    {newCase.parties.map(party => (
                      <div
                        key={party.id}
                        className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 ${partyRoles.find(r => r.id === party.role)?.color} rounded-full flex items-center justify-center`}>
                            <Users className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{party.name}</p>
                            <p className="text-slate-400 text-sm">
                              {partyRoles.find(r => r.id === party.role)?.name}
                              {party.is_ai && <span className="text-purple-400 mr-2">(AI)</span>}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveParty(party.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-slate-700/30 rounded-lg text-slate-400">
                    لم تتم إضافة أطراف بعد
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setShowNewCaseModal(false)}
                className="flex-1 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-600"
              >
                إلغاء
              </button>
              <button
                onClick={handleCreateCase}
                disabled={!newCase.title || !newCase.case_type}
                className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl hover:from-amber-600 hover:to-amber-700 disabled:opacity-50"
              >
                إنشاء القضية
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Party Modal */}
      {showNewPartyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-slate-800 rounded-2xl p-8 w-full max-w-md mx-4 border border-slate-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">إضافة طرف جديد</h3>
              <button
                onClick={() => setShowNewPartyModal(false)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-slate-300 mb-2">اسم الطرف</label>
                <input
                  type="text"
                  value={newParty.name}
                  onChange={(e) => setNewParty(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                  placeholder="الاسم الكامل"
                />
              </div>
              
              <div>
                <label className="block text-slate-300 mb-2">صفة الطرف</label>
                <select
                  value={newParty.role}
                  onChange={(e) => setNewParty(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                >
                  <option value="">-- اختر الصفة --</option>
                  {partyRoles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-slate-300 mb-2">رقم الهوية</label>
                <input
                  type="text"
                  value={newParty.id_number}
                  onChange={(e) => setNewParty(prev => ({ ...prev, id_number: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                  placeholder="رقم الهوية الوطنية"
                />
              </div>
              
              <div>
                <label className="block text-slate-300 mb-2">البريد الإلكتروني</label>
                <input
                  type="email"
                  value={newParty.email}
                  onChange={(e) => setNewParty(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                  placeholder="example@email.com"
                />
              </div>
              
              <div>
                <label className="block text-slate-300 mb-2">رقم الجوال</label>
                <input
                  type="tel"
                  value={newParty.phone}
                  onChange={(e) => setNewParty(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                  placeholder="05xxxxxxxx"
                />
              </div>
              
              <div className="flex items-center gap-3 p-4 bg-purple-600/20 rounded-xl">
                <input
                  type="checkbox"
                  id="is_ai"
                  checked={newParty.is_ai}
                  onChange={(e) => setNewParty(prev => ({ ...prev, is_ai: e.target.checked }))}
                  className="w-5 h-5 rounded border-purple-500 text-purple-600 focus:ring-purple-500"
                />
                <label htmlFor="is_ai" className="text-slate-300">
                  <span className="font-medium text-purple-400">طرف ذكاء اصطناعي</span>
                  <span className="block text-sm text-slate-400">سيتم محاكاة هذا الطرف بالذكاء الاصطناعي</span>
                </label>
              </div>
            </div>
            
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setShowNewPartyModal(false)}
                className="flex-1 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-600"
              >
                إلغاء
              </button>
              <button
                onClick={handleAddParty}
                disabled={!newParty.name || !newParty.role}
                className="flex-1 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50"
              >
                إضافة الطرف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
